import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Megaphone, CalendarOff, Plus, Edit2, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';

export default function CommsTab({
  announcements, openCreateAnnouncement, openEditAnnouncement, handleToggleAnnouncement, handleDeleteAnnouncement,
  closures, closureForm, setClosureForm, closureSaving, handleAddClosure, handleDeleteClosure,
}) {
  return (
    <div className="space-y-5">
      <Card className="border-[#DED8EB] shadow-sm" data-testid="announcements-settings">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Megaphone className="h-4 w-4 text-[#6B3FA0]" /> Announcements
            </CardTitle>
            <Button size="sm" onClick={openCreateAnnouncement} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-8" data-testid="create-announcement-btn">
              <Plus className="h-3.5 w-3.5" /> New
            </Button>
          </div>
          <p className="text-xs text-[#8E85A0]">Visible to all staff on their dashboard. Set priority and expiry.</p>
        </CardHeader>
        <CardContent>
          {announcements.length === 0 ? (
            <p className="text-sm text-[#8E85A0] text-center py-4">No announcements yet</p>
          ) : (
            <div className="space-y-2">
              {announcements.map(a => (
                <div key={a.id} className={`flex items-center justify-between p-3 rounded-lg border ${a.is_active ? 'border-[#DED8EB] bg-white' : 'border-gray-200 bg-gray-50 opacity-60'}`} data-testid={`announcement-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#2D1B4E]">{a.title}</span>
                      <Badge className={`text-[9px] ${a.priority === 'urgent' ? 'bg-red-100 text-red-700' : a.priority === 'important' ? 'bg-[#FFF3E0] text-[#E65100]' : 'bg-[#F0EDF6] text-[#5A4D6E]'}`}>
                        {a.priority}
                      </Badge>
                      {!a.is_active && <Badge className="text-[9px] bg-gray-200 text-gray-600">Inactive</Badge>}
                    </div>
                    {a.body && <p className="text-xs text-[#8E85A0] truncate mt-0.5">{a.body}</p>}
                    <p className="text-[10px] text-[#8E85A0] mt-0.5">By {a.created_by} · {a.expires_at ? `Expires ${a.expires_at}` : 'No expiry'}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    <Button variant="ghost" size="icon" onClick={() => handleToggleAnnouncement(a)} className="h-7 w-7 text-[#8E85A0]" title={a.is_active ? 'Deactivate' : 'Activate'}>
                      {a.is_active ? <ToggleRight className="h-3.5 w-3.5" /> : <ToggleLeft className="h-3.5 w-3.5" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEditAnnouncement(a)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]"><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteAnnouncement(a.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#DED8EB] shadow-sm" data-testid="closures-settings">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <CalendarOff className="h-4 w-4 text-[#D9534F]" /> Closures
          </CardTitle>
          <p className="text-xs text-[#8E85A0]">Mark days the program is closed. Visible to all staff.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Input type="date" value={closureForm.date} onChange={e => setClosureForm(p => ({ ...p, date: e.target.value }))}
              className="border-[#DED8EB] w-44" data-testid="closure-date" />
            <Input value={closureForm.reason} onChange={e => setClosureForm(p => ({ ...p, reason: e.target.value }))}
              placeholder="Reason (e.g., Holiday)" className="border-[#DED8EB] flex-1" data-testid="closure-reason" />
            <Button onClick={handleAddClosure} disabled={closureSaving || !closureForm.date} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 shrink-0" data-testid="add-closure-btn">
              <Plus className="h-4 w-4" /> Add
            </Button>
          </div>
          {closures.length === 0 ? (
            <p className="text-sm text-[#8E85A0] text-center py-2">No closures scheduled</p>
          ) : (
            <div className="space-y-1.5">
              {closures.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2.5 rounded-lg bg-red-50 border border-red-200" data-testid={`closure-${c.id}`}>
                  <div className="flex items-center gap-2">
                    <CalendarOff className="h-4 w-4 text-[#D9534F]" />
                    <span className="text-sm font-semibold text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{c.date}</span>
                    {c.reason && <span className="text-sm text-[#5A4D6E]">— {c.reason}</span>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteClosure(c.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
